import React, { useState, useEffect, useCallback } from "react";
import { CiCircleRemove } from "react-icons/ci";
import { useAppSelector } from "../../../../../app/hooks";
import { selectDashboard } from "../../../../../features/dashboards/dashboardSlice";
import GroupImageSelect, { GroupImage, Status } from "./GroupImageSelect";
import MarkerCoordinates, { Coordinates } from "./MarkerCoordinates";
import NotificationSelectInput from "./NotificationSelectInput";
import MarkerSize, { Size } from "./MarkerSize";
import { MarkerData } from "../..";
import { Tooltip } from "flowbite-react";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { useGetNotificationByIDQuery } from "../../../../../services/groupNotificationAPI";
import useGetLanguage from "../../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../../dictionary/ChartsText";

export enum MapType {
  LAND = "Land",
  IMAGE = "Image",
}

type NotificationMarkerEditProps = {
  mapType: MapType;
  dashboardID: string;
  marker: MarkerData;
  index: number;
  updateMarker: (marker: MarkerData, index: number) => void;
  removeMarker: (index: number) => void;
};

const NotificationMarkerEdit: React.FC<NotificationMarkerEditProps> = ({
  mapType,
  dashboardID = "",
  marker: {
    notificationID: initialNotificationID = "",
    lat: initialLat = 0,
    lng: initialLng = 0,
    activeStatusImageId: initialActiveStatusImageId = "",
    inactiveStatusImageId: initialInactiveStatusImageId = "",
    activeAcknowledgedImageId: initialActiveAcknowledgedImageId = "",
    markerSize: initialMarkerSize = Size.Medium,
    ...rest
  },
  index,
  updateMarker,
  removeMarker,
}) => {
  const language: LanguageText = useGetLanguage({
    fileName: "ChartsText",
  });
  const dashboard = useAppSelector((state) => selectDashboard(state, dashboardID));
  const groupID: string = dashboard?.group_id ?? "";

  const [notificationID, setNotificationID] = useState<string | "">(
    initialNotificationID
  );

  const [location, setLocation] = useState<Coordinates | null>({
    lat: initialLat,
    lng: initialLng,
  });
  const [activeImageID, setActiveImageID] = useState<string | "">(
    initialActiveStatusImageId
  );
  const [inactiveImageID, setInactiveImageID] = useState<string | "">(
    initialInactiveStatusImageId
  );
  const [activeAcknowledgedImageId, setActiveAcknowledgeImageId] = useState<string | "">(
    initialActiveAcknowledgedImageId
  );
  const [markerSize, setMarkerSize] = useState<Size>(initialMarkerSize as Size);

  const handleRemoveMarker = () => {
    removeMarker(index);
  };
  const handleNotificationIDChange = useCallback(
    (newNotificationID: string) => {
      setNotificationID(newNotificationID);
      updateMarker(
        {
          ...rest,
          notificationID: newNotificationID, // Updates only notificationID
          lat: initialLat,
          lng: initialLng,
          activeStatusImageId: initialActiveStatusImageId,
          inactiveStatusImageId: initialInactiveStatusImageId,
          activeAcknowledgedImageId: initialActiveAcknowledgedImageId,
          markerSize: markerSize,
        },
        index
      );
    },
    [index, rest]
  );

  const handleSizeChange = (size: Size) => {
    setMarkerSize(size);
    updateMarker(
      {
        ...rest,
        notificationID: initialNotificationID,
        lat: initialLat,
        lng: initialLng,
        activeStatusImageId: initialActiveStatusImageId,
        inactiveStatusImageId: initialInactiveStatusImageId,
        activeAcknowledgedImageId: initialActiveAcknowledgedImageId,
        markerSize: size,
      },
      index
    );
  };

  const handleLocationChange = useCallback(
    (newLocation: Coordinates) => {
      setLocation(newLocation);
      updateMarker(
        {
          ...rest,
          ...newLocation, // Updates only location
          notificationID: initialNotificationID,
          activeStatusImageId: initialActiveStatusImageId,
          inactiveStatusImageId: initialInactiveStatusImageId,
          activeAcknowledgedImageId: initialActiveAcknowledgedImageId,
          markerSize: markerSize,
        },
        index
      );
    },
    [index, rest]
  );

  const handleActiveImageSelect = useCallback(
    (imageID: string) => {
      setActiveImageID(imageID);
      updateMarker(
        {
          ...rest,
          notificationID: initialNotificationID,
          lat: initialLat,
          lng: initialLng,
          activeStatusImageId: imageID, // Updates only active imageID
          inactiveStatusImageId: initialInactiveStatusImageId,
          activeAcknowledgedImageId: initialActiveAcknowledgedImageId,
          markerSize: markerSize,
        },
        index
      );
    },
    [index, rest]
  );

  const handleInactiveImageSelect = useCallback(
    (imageID: string) => {
      setInactiveImageID(imageID);
      updateMarker(
        {
          ...rest,
          notificationID: initialNotificationID,
          lat: initialLat,
          lng: initialLng,
          activeStatusImageId: initialActiveStatusImageId,
          inactiveStatusImageId: imageID, // Updates only inactive imageID
          activeAcknowledgedImageId: initialActiveAcknowledgedImageId,
          markerSize: markerSize,
        },
        index
      );
    },
    [index, rest]
  );
  const handleActiveAcknowledgedImageSelect = useCallback(
    (imageID: string) => {
      setActiveAcknowledgeImageId(imageID);
      updateMarker(
        {
          ...rest,
          notificationID: initialNotificationID,
          lat: initialLat,
          lng: initialLng,
          activeStatusImageId: initialActiveStatusImageId,
          inactiveStatusImageId: initialInactiveStatusImageId,
          activeAcknowledgedImageId: imageID, // Updates only activeAcknowledged imageID
          markerSize: markerSize,
        },
        index
      );
    },
    [index, rest]
  );
  const {
    data: groupNotificationData,
    isLoading,
    error,
  } = useGetNotificationByIDQuery(initialNotificationID, {
    skip: !initialNotificationID,
    pollingInterval: 1000 * 10,
  });

  useEffect(() => {
    setNotificationID(initialNotificationID);
    setLocation({ lat: initialLat, lng: initialLng });
    setActiveImageID(initialActiveStatusImageId);
    setInactiveImageID(initialInactiveStatusImageId);
    setActiveAcknowledgeImageId(initialActiveAcknowledgedImageId);
    setMarkerSize(initialMarkerSize as Size);
  }, [
    initialNotificationID,
    initialLat,
    initialLng,
    initialActiveStatusImageId,
    initialInactiveStatusImageId,
    initialActiveAcknowledgedImageId,
    initialMarkerSize,
  ]);

  // This is to auto-remove any marker that has a notification deleted or invalidated
  useEffect(() => {
    if (error) {
      removeMarker(index);
    }
  }, [error, index, removeMarker]);

  return (
    <div className="relative mb-6 p-10 rounded-xl mt-2 bg-gray-200">
      <Tooltip
        placement="bottom-end"
        style="light"
        trigger="hover"
        content={
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm text-sm text-gray-700 border border-gray-200">
            <div className="mb-4">
              <h3 className="text-base text-center font-semibold text-blue-600">
                {language?.charts?.map?.markers?.notification?.info?.title}
              </h3>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-medium text-blue-500">
                {" "}
                {language?.charts?.map?.markers?.notification?.info?.customImage?.title}
              </h4>
              <p className="text-xs">
                {language?.charts?.map?.markers?.notification?.info?.customImage?.content}
              </p>

              <h4 className="text-sm font-medium text-blue-500">
                {" "}
                {language?.charts?.map?.markers?.notification?.info?.positioning?.title}
              </h4>
              <p className="text-xs">
                {language?.charts?.map?.markers?.notification?.info?.positioning?.content}
              </p>
            </div>
          </div>
        }
      >
        <BsFillInfoCircleFill
          size={20}
          color="grey"
          className="absolute top-2 left-2 cursor-pointer mb-5"
        />
      </Tooltip>

      <CiCircleRemove
        size={30}
        color="grey-500"
        className="absolute top-2 right-2 cursor-pointer"
        title="Remove"
        onClick={handleRemoveMarker}
      />
      <NotificationSelectInput
        groupID={groupID}
        currentNotificationID={notificationID}
        onNotificationChange={handleNotificationIDChange}
      />
      {mapType === MapType.LAND && notificationID !== "" && (
        <MarkerCoordinates
          initialLocation={location}
          onLocationChange={handleLocationChange}
        />
      )}
      {notificationID !== "" && (
        <GroupImageSelect
          status={Status.ACTIVE}
          groupID={groupID}
          currentImageID={activeImageID}
          onImageSelect={handleActiveImageSelect}
        />
      )}
      {notificationID !== "" && (
        <GroupImageSelect
          status={Status.INACTIVE}
          groupID={groupID}
          currentImageID={inactiveImageID}
          onImageSelect={handleInactiveImageSelect}
        />
      )}
      {notificationID !== "" && groupNotificationData?.configuration?.latch_on && (
        <GroupImageSelect
          status={Status.ACKNOWLEDGE}
          groupID={groupID}
          currentImageID={activeAcknowledgedImageId}
          onImageSelect={handleActiveAcknowledgedImageSelect}
        />
      )}
      {notificationID !== "" && (
        <MarkerSize initialSize={markerSize} onSizeChange={handleSizeChange} />
      )}
    </div>
  );
};

export const getNewNotificationMarkerData = (
  imgWidth?: number,
  imgHeight?: number
): Partial<MarkerData> => {
  return {
    markerType: "notification",
    notificationID: "",
    lat: imgWidth ? imgWidth / 2 : 0,
    lng: imgHeight ? imgHeight / 2 : 0,
    activeStatusImageId: "",
    inactiveStatusImageId: "",
    activeAcknowledgedImageId: "",
    markerSize: Size.Medium,
  };
};

export default NotificationMarkerEdit;
